/**
 * Centralized status configuration for quotes
 * Single source of truth for status types, colors, labels, and values
 */

export type QuoteStatusType = 'draft' | 'sent' | 'negotiating' | 'won' | 'lost';

/**
 * Status color configuration
 * Used for icons, dots, badges, and backgrounds
 */
export interface StatusColors {
  /** Primary color for the status (icon background, dots) */
  primary: string;
  /** Background color for badges */
  badgeBackground: string;
  /** Text color for badges */
  badgeText: string;
}

/**
 * Complete status configuration
 */
export interface StatusConfig {
  type: QuoteStatusType;
  value: string;
  label: string;
  colors: StatusColors;
}

/**
 * Status colors map - use this for quick color lookups
 */
export const STATUS_COLORS: Record<QuoteStatusType, StatusColors> = {
  draft: {
    primary: '#20262D',
    badgeBackground: '#F5F5F5',
    badgeText: '#616161'
  },
  sent: {
    primary: '#1E96FC',
    badgeBackground: '#E3F2FD',
    badgeText: '#1565C0'
  },
  negotiating: {
    primary: '#F08B0F',
    badgeBackground: '#FFF3E0',
    badgeText: '#E65100'
  },
  won: {
    primary: '#23A566',
    badgeBackground: '#E8F5E9',
    badgeText: '#2E7D32'
  },
  lost: {
    primary: '#FA2020',
    badgeBackground: '#FFEBEE',
    badgeText: '#C62828'
  }
};

/**
 * Status options for dropdowns and selectors
 */
export const STATUS_OPTIONS: StatusConfig[] = [
  {
    type: 'draft',
    value: 'Draft',
    label: 'Draft',
    colors: STATUS_COLORS.draft
  },
  {
    type: 'sent',
    value: 'Sent',
    label: 'Sent',
    colors: STATUS_COLORS.sent
  },
  {
    type: 'negotiating',
    value: 'Negotiating',
    label: 'Negotiating',
    colors: STATUS_COLORS.negotiating
  },
  {
    type: 'won',
    value: 'Won (Closed)',
    label: 'Won (Closed)',
    colors: STATUS_COLORS.won
  },
  {
    type: 'lost',
    value: 'Lost (Closed)',
    label: 'Lost (Closed)',
    colors: STATUS_COLORS.lost
  }
];

/**
 * Get status type from status string value
 */
export const getStatusType = (
  status: string | null | undefined
): QuoteStatusType | null => {
  if (!status) return null;
  const normalizedStatus = status.toLowerCase();

  const found = STATUS_OPTIONS.find(
    opt => opt.value.toLowerCase() === normalizedStatus
  );

  return found?.type || null;
};

/**
 * Get status colors by status type
 */
export const getStatusColors = (type: QuoteStatusType): StatusColors =>
  STATUS_COLORS[type];

/**
 * Get status colors by status string (for legacy support)
 * Falls back to draft colors if status not found
 */
export const getStatusColorsByValue = (
  status: string | null | undefined
): StatusColors => {
  const type = getStatusType(status);
  return type ? STATUS_COLORS[type] : STATUS_COLORS.draft;
};

/**
 * Get the primary/dot color for a status type
 */
export const getDotColor = (type: QuoteStatusType): string =>
  STATUS_COLORS[type].primary;

/**
 * Get color scheme for quote status badge (legacy compatibility)
 * Returns backgroundColor, textColor, and dotColor based on status string
 */
export const getQuoteStatusColor = (
  status: string
): {
  backgroundColor: string;
  textColor: string;
  dotColor: string;
} => {
  const normalizedStatus = status?.toLowerCase() || '';

  // Approved - Green (special case not in main options)
  if (
    normalizedStatus.includes('approved') &&
    !normalizedStatus.includes('pending')
  ) {
    return {
      backgroundColor: STATUS_COLORS.won.badgeBackground,
      textColor: STATUS_COLORS.won.badgeText,
      dotColor: '#4CAF50'
    };
  }

  // Pending Approval - Orange/Yellow (special case)
  if (normalizedStatus.includes('pending')) {
    return {
      backgroundColor: STATUS_COLORS.negotiating.badgeBackground,
      textColor: STATUS_COLORS.negotiating.badgeText,
      dotColor: '#FF9800'
    };
  }

  // Saved/Draft - Gray
  if (
    normalizedStatus.includes('saved') ||
    normalizedStatus.includes('draft')
  ) {
    return {
      backgroundColor: STATUS_COLORS.draft.badgeBackground,
      textColor: STATUS_COLORS.draft.badgeText,
      dotColor: '#9E9E9E'
    };
  }

  // Sent - Blue
  if (normalizedStatus.includes('sent')) {
    return {
      backgroundColor: STATUS_COLORS.sent.badgeBackground,
      textColor: STATUS_COLORS.sent.badgeText,
      dotColor: '#2196F3'
    };
  }

  // Rejected/Lost - Red
  if (
    normalizedStatus.includes('reject') ||
    normalizedStatus.includes('lost')
  ) {
    return {
      backgroundColor: STATUS_COLORS.lost.badgeBackground,
      textColor: STATUS_COLORS.lost.badgeText,
      dotColor: '#F44336'
    };
  }

  // Won
  if (normalizedStatus.includes('won')) {
    return {
      backgroundColor: STATUS_COLORS.won.badgeBackground,
      textColor: STATUS_COLORS.won.badgeText,
      dotColor: STATUS_COLORS.won.primary
    };
  }

  // Negotiating
  if (normalizedStatus.includes('negotiating')) {
    return {
      backgroundColor: STATUS_COLORS.negotiating.badgeBackground,
      textColor: STATUS_COLORS.negotiating.badgeText,
      dotColor: STATUS_COLORS.negotiating.primary
    };
  }

  // Default - Gray
  return {
    backgroundColor: STATUS_COLORS.draft.badgeBackground,
    textColor: STATUS_COLORS.draft.badgeText,
    dotColor: '#9E9E9E'
  };
};
